import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { BarChart3, ChevronDown, Download, MoreHorizontal, Plus } from 'lucide-react';
import { PaginationControls, usePagination, paginateData } from '@/shared/components/pagination';
import { ObservationsTable } from './ObservationsTable';
import { ObservationsFilters } from './ObservationsFilters';
import { ObservationsDashboard } from './ObservationsDashboard';
import { ObservationCreateDialog } from './ObservationCreateDialog';
import {
  useObservation,
  useObservations,
  useObservationMutations,
  useObservationStatusSummary,
} from '../hooks';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  formatLocationLabel,
  type ObservationFilters as ObservationFiltersType,
  type ObservationSortState,
  type ObservationSummary,
} from '../types';
// Importing for the side effects: ``registerTour`` runs at module load
// for each of the observations tour variants.
import { OBSERVATIONS_LIST_TOUR_ID } from '@/features/tours/observations/list';
import { OBSERVATIONS_COMBINED_TOUR_ID } from '@/features/tours/observations/combined';
import '@/features/tours/observations/detail';
import { useTour } from '@/features/tours';
import { sortListItemsByRules } from '@/shared/utils/listSorting';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';

const exportObservationsToCsv = (items: ObservationSummary[]) => {
  const headers = [
    'Number',
    'Title',
    'Status',
    'Priority',
    'Assignee',
    'Type',
    'Location',
    'Due Date',
    'Created',
  ];
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const rows = items.map((item) =>
    [
      `OBS-${item.number}`,
      item.title,
      STATUS_LABELS[item.status],
      PRIORITY_LABELS[item.priority],
      item.assignee ? `${item.assignee.first_name} ${item.assignee.last_name}` : '',
      item.observation_type?.name ?? '',
      item.location
        ? [
            formatLocationLabel(item.location),
            ...(item.location.area_tag_sets ?? []).map((areaTagSet) => areaTagSet.name),
          ].join(' / ')
        : '',
      item.due_date ?? '',
      item.created_at,
    ]
      .map((cell) => escape(String(cell)))
      .join(',')
  );
  const csv = [headers.map(escape).join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `observations-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const ObservationsPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | undefined>();
  const [filters, setFilters] = useState<ObservationFiltersType>({});
  const [sort, setSort] = useState<ObservationSortState[]>([]);
  const [summaryExpandedOverride, setSummaryExpandedOverride] = useState<boolean | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  // The same React-Query call ``ObservationsTable`` makes is reused here;
  // React Query dedupes it.
  const { data: items, isLoading, error } = useObservations(numericProjectId, filters);
  const { data: editingItem } = useObservation(editingItemId, numericProjectId);
  const { data: allProjectItems } = useObservations(numericProjectId, {});
  const { data: statusSummary } = useObservationStatusSummary(numericProjectId);
  const { deleteItem } = useObservationMutations(numericProjectId);

  const allItems = useMemo(() => items ?? [], [items]);
  const sortedItems = useMemo(() => sortObservationItems(allItems, sort), [allItems, sort]);
  const totalCount = useMemo(
    () => (statusSummary ?? []).reduce((sum, entry) => sum + entry.count, 0),
    [statusSummary]
  );
  const filteredCount = allItems.length;
  const areaTagOptions = useMemo(() => {
    const locations = (allProjectItems ?? []).flatMap((item) =>
      item.location ? [item.location] : []
    );
    return Array.from(new Map(locations.map((location) => [location.id, location])).values()).sort(
      (a, b) =>
        `${a.layout_name} ${a.name}`.localeCompare(`${b.layout_name} ${b.name}`, undefined, {
          numeric: true,
        })
    );
  }, [allProjectItems]);
  const areaTagSetOptions = useMemo(() => {
    const areaTagSets = (allProjectItems ?? []).flatMap(
      (item) => item.location?.area_tag_sets ?? []
    );
    return Array.from(
      new Map(areaTagSets.map((areaTagSet) => [areaTagSet.id, areaTagSet])).values()
    ).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [allProjectItems]);

  const [paginationState, paginationActions, paginationInfo] = usePagination(filteredCount, 10);
  const pagedItems = useMemo(
    () => paginateData(sortedItems, paginationState.currentPage, paginationState.itemsPerPage),
    [sortedItems, paginationState.currentPage, paginationState.itemsPerPage]
  );

  const handleFiltersChange = (next: ObservationFiltersType) => {
    setFilters(next);
    paginationActions.setCurrentPage(1);
  };

  const handleSortChange = (next: ObservationSortState[]) => {
    setSort(next);
    paginationActions.setCurrentPage(1);
  };

  // Auto-start the appropriate Observations tour:
  //   - items exist → start the *combined* tour (list → click row → detail)
  //   - no items yet → start the list-only tour to introduce the feature
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  const hasItems = allItems.length >= 1;
  const isSummaryVisible = summaryExpandedOverride ?? !isMobileViewport;
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (hasItems) {
      if (hasSeen(OBSERVATIONS_COMBINED_TOUR_ID)) return;
      if (hasSeen(OBSERVATIONS_LIST_TOUR_ID)) return;
      void startTour(OBSERVATIONS_COMBINED_TOUR_ID);
    } else {
      if (hasSeen(OBSERVATIONS_LIST_TOUR_ID)) return;
      void startTour(OBSERVATIONS_LIST_TOUR_ID);
    }
  }, [tourHydrated, active, startTour, hasSeen, hasItems]);

  const handleRowClick = (item: ObservationSummary) => {
    navigate(item.id);
  };

  const handleDelete = async (item: ObservationSummary) => {
    if (!window.confirm(`Delete OBS-${item.number} “${item.title}”? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync(item.id);
      toast({ title: 'Observation deleted' });
    } catch (deleteError) {
      toast({
        title: 'Unable to delete observation',
        description: handleApiError(deleteError),
        variant: 'destructive',
      });
    }
  };

  return (
    <BaseLayout>
      <div className="min-w-0 space-y-6 p-4 md:p-6">
        {/* Page header */}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Observations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Track and close field observations.
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
              aria-expanded={isSummaryVisible}
              aria-controls="observations-summary"
              data-tour-id="observations-summary-toggle"
              onClick={() =>
                setSummaryExpandedOverride((current) => !(current ?? !isMobileViewport))
              }
            >
              <BarChart3 className="h-4 w-4" />
              {isSummaryVisible ? 'Hide summary' : 'Show summary'}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
          <div
            className={`grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center ${
              isSummaryVisible ? 'order-2 sm:order-none' : ''
            }`}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={filteredCount === 0}
                >
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                <DropdownMenuItem onSelect={() => exportObservationsToCsv(allItems)}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => setCreateOpen(true)}
              disabled={!numericProjectId}
              data-tour-id="observations-new"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Observation
            </Button>
          </div>

          {isSummaryVisible && (
            <div
              id="observations-summary"
              className="order-1 min-w-0 sm:order-none sm:col-span-2 sm:mt-3"
            >
              <ObservationsDashboard projectId={numericProjectId} />
            </div>
          )}
        </div>

        <ObservationsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          totalCount={totalCount}
          filteredCount={filteredCount}
          sort={sort}
          onSortChange={handleSortChange}
          areaTags={areaTagOptions}
          areaTagSets={areaTagSetOptions}
        />

        <ObservationsTable
          items={pagedItems}
          isLoading={isLoading}
          error={error}
          onRowClick={handleRowClick}
          onEdit={(item) => setEditingItemId(item.id)}
          onDelete={(item) => void handleDelete(item)}
        />

        {!isLoading && !error && filteredCount > 0 && (
          <PaginationControls
            state={paginationState}
            actions={paginationActions}
            info={paginationInfo}
          />
        )}
      </div>

      <ObservationCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={numericProjectId}
      />
      <ObservationCreateDialog
        open={!!editingItemId && !!editingItem}
        onClose={() => setEditingItemId(undefined)}
        projectId={numericProjectId}
        item={editingItem}
      />
    </BaseLayout>
  );
};

const OBSERVATION_PRIORITY_ORDER = { LOW: 0, MEDIUM: 1, HIGH: 2 } as const;

export const sortObservationItems = (
  items: ObservationSummary[],
  sort: readonly ObservationSortState[]
) => {
  return sortListItemsByRules(items, sort, (item, key) => {
    switch (key) {
      case 'number':
        return item.number;
      case 'status':
        return STATUS_LABELS[item.status];
      case 'priority':
        return OBSERVATION_PRIORITY_ORDER[item.priority];
      case 'assignee':
        return item.assignee ? `${item.assignee.first_name} ${item.assignee.last_name}` : null;
      case 'due_date':
        return item.due_date ? Date.parse(item.due_date) : null;
      case 'created_at':
        return Date.parse(item.created_at);
    }
  });
};
